import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import {
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ChartBarIcon,
  FolderIcon,
  ScaleIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { SEOMeta } from '@/templates/SEOMeta';
import { SeoInternalLinks } from '@/components/SeoInternalLinks';
import { getI18nProps } from '@/utils/i18n';

export default function InfluencerMarketing({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations('influencerMarketing');

  const stats = [
    { value: t('stats.higherROI.value'), label: t('stats.higherROI.label') },
    { value: t('stats.trust.value'), label: t('stats.trust.label') },
    { value: t('stats.engagement.value'), label: t('stats.engagement.label') }
  ];

  const features = [
    {
      icon: CalendarIcon,
      title: t('features.campaignAutomation.title'),
      description: t('features.campaignAutomation.description')
    },
    {
      icon: ChartBarIcon,
      title: t('features.performanceAnalytics.title'),
      description: t('features.performanceAnalytics.description')
    },
    {
      icon: FolderIcon,
      title: t('features.contentLibrary.title'),
      description: t('features.contentLibrary.description')
    },
    {
      icon: CurrencyDollarIcon,
      title: t('features.paymentProcessing.title'),
      description: t('features.paymentProcessing.description')
    }
  ];

  const successStories = [
    {
      industry: t('successStories.fashionBrand.industry'),
      results: t('successStories.fashionBrand.results')
    },
    {
      industry: t('successStories.techStartup.industry'),
      results: t('successStories.techStartup.results')
    },
    {
      industry: t('successStories.foodBeverage.industry'),
      results: t('successStories.foodBeverage.results')
    }
  ];

  return (
      <>
        <SEOMeta
            title={seoMeta?.title || t('seo.title')}
            description={seoMeta?.description || t('seo.description')}
            keywords={seoMeta?.keywords || t('seo.keywords')}
            img="/influencer-marketing.png"
            url={seoMeta?.url || "/influencer-marketing"}
        />

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero Section */}
          <section className="pt-28 pb-24 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
              >
                {t('hero.title')}
              </motion.h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                {t('hero.subtitle')}
              </p>
              <Button
                  onClick={() => router.push('/signup')}
                  className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
              >
                {t('hero.cta')}
              </Button>
            </div>
          </section>

          {/* Image Section */}
          <section className="py-24">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('image.title')}</h2>
              </div>
              <div className="rounded-xl overflow-hidden flex items-center justify-center">
                <img
                    src="/influencer-marketing.png"
                    alt={t('image.alt')}
                    className="w-full max-w-4xl mx-auto"
                />
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('statsSection.title')}</h2>
                <p className="text-gray-600 max-w-3xl mx-auto">
                  {t('statsSection.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {stats.map((stat, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-gray-50 p-8 rounded-2xl text-center"
                    >
                      <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-2">
                        {stat.value}
                      </p>
                      <p className="text-lg">{stat.label}</p>
                    </motion.div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">{t('reasons.title')}</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="bg-purple-100 rounded-full p-2 mr-4">
                        <UserGroupIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-bold">{t('reasons.trust.title')}</p>
                        <p>{t('reasons.trust.text')}</p>
                      </div>
                    </li>

                    <li className="flex items-start">
                      <div className="bg-purple-100 rounded-full p-2 mr-4">
                        <RocketLaunchIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-bold">{t('reasons.reach.title')}</p>
                        <p>{t('reasons.reach.text')}</p>
                      </div>
                    </li>

                    <li className="flex items-start">
                      <div className="bg-purple-100 rounded-full p-2 mr-4">
                        <ChatBubbleLeftRightIcon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-bold">{t('reasons.authenticity.title')}</p>
                        <p>{t('reasons.authenticity.text')}</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-8 rounded-2xl">
                  <h3 className="text-xl font-semibold mb-4">{t('insights.title')}</h3>
                  <ul className="space-y-3">
                    <li className="flex">
                      <span className="text-purple-600 font-bold mr-2">89%</span>
                      <span>{t('insights.marketers')}</span>
                    </li>
                    <li className="flex">
                      <span className="text-purple-600 font-bold mr-2">65%</span>
                      <span>{t('insights.discovery')}</span>
                    </li>
                    <li className="flex">
                      <span className="text-purple-600 font-bold mr-2">8.7%</span>
                      <span>{t('insights.nano')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('featuresSection.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">{t('featuresSection.subtitle')}</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-white p-8 rounded-2xl shadow-sm"
                    >
                      <div className="mb-4">
                        <feature.icon className="w-12 h-12 text-purple-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Success Stories */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('successSection.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">{t('successSection.subtitle')}</p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {successStories.map((story, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl border border-purple-100"
                    >
                      <h3 className="text-xl font-bold mb-4">{story.industry}</h3>
                      <p className="text-gray-700">{story.results}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonial */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <div className="space-y-6 text-lg">
                <p>{t('testimonial.p1')}</p>
                <p>{t('testimonial.p2')}</p>
                <p>{t('testimonial.p3')}</p>
                <p>{t('testimonial.p4')}</p>
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-24">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.95 }}
                  whileInView={{ scale: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white"
              >
                <h2 className="text-4xl font-bold mb-6">{t('finalCta.title')}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">{t('finalCta.subtitle')}</p>
                <div className="flex justify-center">
                  <Button gray onClick={() => router.push('/signup')}>
                    {t('finalCta.cta')}
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          <SeoInternalLinks />
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  return getI18nProps(ctx);
}
